import React, { useId } from "react";
import { SoonBadge } from "../../atoms/SoonBadge";
import {
  Badge,
  Card,
  CheckBox,
  CheckCard,
  CheckDescription,
  CheckIconTile,
  CheckLabel,
  Description,
  Title,
} from "./ChoiceCard.styles";
import type { ChoiceCardMultipleProps, ChoiceCardProps, ChoiceCardSingleProps } from "./ChoiceCard.types";

/** `single`: pressing the card picks it. */
const SingleChoiceCard: React.FC<ChoiceCardSingleProps> = ({
  title,
  description,
  icon,
  iconTone = "accent",
  selected,
  onSelect,
  soon = false,
  className,
}) => {
  const id = useId();
  // The title alone names the button; the detail line is its description.
  return (
    <Card
      type="button"
      className={className}
      $selected={selected}
      aria-pressed={selected}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      aria-disabled={soon ? "true" : undefined}
      $soon={soon}
      onClick={soon ? undefined : onSelect}
    >
      <Badge $tone={iconTone} aria-hidden="true">
        {icon}
      </Badge>
      <Title id={`${id}-title`}>
        {title}
        {soon ? <SoonBadge /> : null}
      </Title>
      <Description id={`${id}-description`}>{description}</Description>
    </Card>
  );
};

/** `multiple`: a checkbox in the card's corner; the whole card toggles it. */
const MultipleChoiceCard: React.FC<ChoiceCardMultipleProps> = ({
  id,
  title,
  description,
  icon,
  checked,
  disabled = false,
  checkedTint = "accent",
  onChange,
  soon = false,
  className,
}) => {
  const isDisabled = disabled || soon;
  return (
    <CheckCard htmlFor={id} className={className} $checked={checked} $disabled={disabled} $tint={checkedTint}>
      <CheckBox
        id={id}
        type="checkbox"
        checked={checked}
        disabled={isDisabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <CheckIconTile $checked={checked} aria-hidden="true">
        {icon}
      </CheckIconTile>
      <CheckLabel>
        {title}
        {soon ? <SoonBadge /> : null}
      </CheckLabel>
      <CheckDescription>{description}</CheckDescription>
    </CheckCard>
  );
};

/** An option as a card: icon badge, title and a line of detail, picked singly or ticked with others. */
export const ChoiceCard: React.FC<ChoiceCardProps> = (props) =>
  props.selection === "multiple" ? <MultipleChoiceCard {...props} /> : <SingleChoiceCard {...props} />;

export default ChoiceCard;
